import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  accountKeys,
  billingKeys,
  deriveSubscriptionState,
  isTauri,
  onWindowFocus,
  useAccounts,
  useAuthStore,
  useBillingStatus,
  useCancelSubscriptions,
  useCheckoutUrl,
  useRedeemCode,
  useResyncBilling,
  useSeatCheckout,
  type AlreadySubscribedDetails,
  type PlanSwitchDetails,
  type BillingTier,
  type SubscriptionSummary,
} from '@wordpuppi/shared';
import { openBillingLink, startCheckout } from '@/utils/desktopActions';
import { confirmDialog } from '@/lib/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { PaymentTrust } from '@/components/PaymentTrust';
import { toast } from 'sonner';
import { Check, CircleCheck, ExternalLink, RefreshCw, TriangleAlert, X } from 'lucide-react';
import logoBlack from '@/assets/logo-black-300w.webp';
import logoWhite from '@/assets/logo-white-300w.webp';

export const Route = createFileRoute('/account/billing')({
  component: BillingPage,
});

/** Survives the round-trip to RevenueCat's hosted checkout and back (AB#450). */
const PENDING_UPGRADE_KEY = 'wpp:pendingUpgrade';

const TIERS: {
  id: BillingTier;
  name: string;
  blurb: string;
  features: string[];
}[] = [
  {
    id: 'basic',
    name: 'Basic',
    blurb: 'For getting your first site live.',
    features: ['Up to 3 sites', 'SEO-first publishing', 'Email support'],
  },
  {
    id: 'pro',
    name: 'Pro',
    blurb: 'For growing sites and distribution.',
    features: ['Up to 10 sites', 'Content distribution', 'Priority support'],
  },
  {
    id: 'ultimate',
    name: 'Ultimate',
    blurb: 'For agencies and many sites.',
    features: ['Unlimited sites', 'Theme variants with shareable preview links', 'Everything in Pro'],
  },
];

/**
 * If a tier's checkout query failed with 409 `already_subscribed` (AB#472) — the
 * plan still has an active, cancel-at-period-end sub — pull the reactivation
 * details off the thrown ApiError. Returns null for any other outcome.
 */
function alreadySubscribed(error: unknown): AlreadySubscribedDetails | null {
  if (!(error instanceof ApiError) || error.data.code !== 'already_subscribed') return null;
  const d = (error.data.details ?? {}) as Record<string, unknown>;
  const ms = d.current_period_ends_at;
  return {
    current_period_ends_at: typeof ms === 'number' ? ms : null,
    manage_url: typeof d.manage_url === 'string' ? d.manage_url : '',
  };
}

/**
 * If a tier's checkout failed with 409 `plan_switch_required` (AB#467) — the user
 * has an active, renewing sub on a DIFFERENT paid plan — pull the from/to/portal
 * details off the thrown ApiError. Returns null for any other outcome. Plan
 * changes happen in RevenueCat's portal, not by buying a second plan.
 */
function planSwitch(error: unknown): PlanSwitchDetails | null {
  if (!(error instanceof ApiError) || error.data.code !== 'plan_switch_required') return null;
  const d = (error.data.details ?? {}) as Record<string, unknown>;
  return {
    from: typeof d.from === 'string' ? d.from : '',
    to: typeof d.to === 'string' ? d.to : '',
    manage_url: typeof d.manage_url === 'string' ? d.manage_url : '',
  };
}

/**
 * If an ADD-SEAT checkout failed with 409 `seat_already_subscribed` (AB#602) — the
 * account still has a cancel-at-period-end extra seat — pull the reactivation
 * details off the thrown ApiError. Same shape as `AlreadySubscribedDetails`.
 * Returns null for any other outcome.
 */
function seatAlreadySubscribed(error: unknown): AlreadySubscribedDetails | null {
  if (!(error instanceof ApiError) || error.data.code !== 'seat_already_subscribed') return null;
  const d = (error.data.details ?? {}) as Record<string, unknown>;
  const ms = d.current_period_ends_at;
  return {
    current_period_ends_at: typeof ms === 'number' ? ms : null,
    manage_url: typeof d.manage_url === 'string' ? d.manage_url : '',
  };
}

/** Distinct redeem rejection codes → user-facing copy (AB#464). */
const REDEEM_ERRORS: Record<string, string> = {
  invalid: 'Code not recognized',
  expired: 'This code has expired',
  exhausted: 'This code has been fully redeemed',
  already_redeemed: "You've already redeemed a code",
};

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const fmtDate = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString() : null);

/** Store-agnostic deep links for cancelling/managing a store-managed sub (AB#581 §4.8). */
const STORE_MANAGE: Record<string, { url: string; label: string }> = {
  app_store: { url: 'https://apps.apple.com/account/subscriptions', label: 'Manage in App Store' },
  play_store: { url: 'https://play.google.com/store/account/subscriptions', label: 'Manage in Google Play' },
};

/** Human store label — secondary badge, always shown (AB#581 §2). */
function storeLabel(store: string): string {
  if (store === 'app_store') return 'App Store';
  if (store === 'play_store') return 'Play Store';
  return 'Web billing'; // rc_billing | stripe | '' — RC web default
}

function subTitle(sub: SubscriptionSummary): string {
  if (sub.kind === 'plan') return sub.plan ? `${cap(sub.plan)} plan` : 'Subscription';
  if (sub.kind === 'seat') return 'Extra seat';
  return 'Subscription';
}

/**
 * A single active subscription (AB#581). Reuses the Team-seats bordered-row
 * pattern. All external links open via `openBillingLink` (system browser on
 * desktop, new tab on web) — never a bare same-window `<a>` (AB#489 class).
 */
function SubscriptionRow({ sub }: { sub: SubscriptionSummary }) {
  const willNotRenew = sub.auto_renewal_status === 'will_not_renew';
  const billingIssue = sub.status === 'in_billing_retry' || sub.status === 'in_grace_period';
  const trial = sub.status === 'trialing';
  const date = fmtDate(sub.current_period_ends_at);

  const status: { label: string; variant: 'default' | 'secondary' | 'destructive' } = billingIssue
    ? { label: 'Billing issue', variant: 'destructive' }
    : willNotRenew
      ? { label: date ? `Cancels ${date}` : 'Cancels', variant: 'secondary' }
      : trial
        ? { label: 'Trial', variant: 'secondary' }
        : { label: 'Active', variant: 'default' };

  const meta = billingIssue
    ? 'Payment problem — update your card'
    : willNotRenew
      ? date && `Ends ${date}`
      : date && `Renews ${date}`;

  // RC portal link takes priority; store-managed subs fall back to a store deep link.
  const storeManage = STORE_MANAGE[sub.store];
  const action = sub.management_url
    ? { url: sub.management_url, label: willNotRenew ? 'Reactivate' : 'Manage' }
    : storeManage
      ? { url: storeManage.url, label: storeManage.label }
      : null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg p-3">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium">{subTitle(sub)}</p>
          <Badge variant={status.variant} className="font-normal">
            {status.label}
          </Badge>
          <Badge variant="secondary" className="font-normal">
            {storeLabel(sub.store)}
          </Badge>
        </div>
        {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
      </div>
      {action && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => void openBillingLink(action.url)}
          aria-label={`${action.label} (opens in browser)`}
        >
          {action.label}
          <ExternalLink className="size-3.5" />
        </Button>
      )}
    </div>
  );
}

function BillingPage() {
  const { isAuthenticated } = useAuthStore();

  // Set when we bounce out to checkout; polls status on return until the
  // webhook flips the plan, so the new tier shows without a manual refresh.
  const [pendingTier, setPendingTier] = useState<string | null>(() =>
    sessionStorage.getItem(PENDING_UPGRADE_KEY),
  );
  // Plan name once the upgrade webhook lands — drives the branded success panel
  // (replaces the old bare toast) on return from Stripe checkout.
  const [justUpgraded, setJustUpgraded] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { data, isLoading } = useBillingStatus(pendingTier ? 2500 : false);
  // Resolve both checkout URLs up front so the upgrade buttons carry a single
  // stable label and redirect synchronously on click (AB#449).
  const checkoutBasic = useCheckoutUrl('basic', isAuthenticated);
  const checkoutPro = useCheckoutUrl('pro', isAuthenticated);
  const checkoutUltimate = useCheckoutUrl('ultimate', isAuthenticated);
  const { data: accountsData } = useAccounts();
  const seatCheckout = useSeatCheckout();
  const redeem = useRedeemCode();

  const status = data?.data;
  const plan = status?.plan ?? 'free';
  // What the user actually has access to (paid OR comped, AB#464). Drives the
  // displayed tier; `plan` stays the paid-RC value the webhook polling watches.
  const effectivePlan = status?.effective_plan ?? plan;
  const isComp = status?.comp ?? false;

  // The subscriptions card is the page's source of truth (AB#581) — the stored
  // plan is only a label; these flags drive every state below.
  const { activeSubs, planSubs, mismatch, overlap, hasStoreManaged } =
    deriveSubscriptionState(status);

  const resync = useResyncBilling();
  const cancelSubs = useCancelSubscriptions();

  // Accounts the user owns — seats are bought per account (AB#334).
  const ownedAccounts = (accountsData?.data ?? []).filter((a) => a.role === 'owner');

  // Self-heal ONCE per customer, then let the refetch settle the UI. A ref guards
  // the poller from re-firing it. Two drifts trigger it:
  //   • plan mismatch — RC holds a live paid sub but the stored plan is 'free' (#581).
  //   • an owned account whose seats may be stale vs RC (#602). Seats reconcile
  //     ONLY inside resync, and resync had no trigger for a plan-HEALTHY owner —
  //     so a cancelled-but-still-active seat (Rick's: opted out, paid through ~Aug)
  //     showed "0 extra seats" forever. Firing here reconciles seat_subscriptions
  //     from RC truth; invalidating the accounts query refreshes the Team-seats
  //     card (resync itself only refetches billing status, not /accounts).
  const resyncedFor = useRef<string | null>(null);
  const resyncMutateAsync = resync.mutateAsync;
  useEffect(() => {
    if (!status) return;
    if (!mismatch && ownedAccounts.length === 0) return;
    // key on the app_user_id so a genuinely new customer can retry once.
    const key = status.app_user_id ?? 'me';
    if (resyncedFor.current === key) return;
    resyncedFor.current = key;
    void resyncMutateAsync()
      .then(() => queryClient.invalidateQueries({ queryKey: accountKeys.list() }))
      .catch(() => {});
  }, [mismatch, status, ownedAccounts.length, resyncMutateAsync, queryClient]);

  const handleRefreshStatus = () =>
    queryClient.invalidateQueries({ queryKey: billingKeys.status() });

  const handleCancelAll = async () => {
    const n = activeSubs.length;
    const storeNote = hasStoreManaged
      ? ' App Store / Play Store subscriptions can’t be cancelled here — use their Manage link for those.'
      : '';
    const ok = await confirmDialog(
      `Cancel all ${n} subscription${n === 1 ? '' : 's'} on this account? They stay active until the end of their paid periods, then won’t renew.${storeNote}`,
      { title: 'Cancel all subscriptions', destructive: true, confirmLabel: 'Cancel subscriptions', cancelLabel: 'Keep subscriptions' },
    );
    if (!ok) return;
    try {
      const res = await cancelSubs.mutateAsync();
      const uncancellable = res.data?.uncancellable ?? [];
      if (uncancellable.length > 0) {
        toast.success(
          "Web subscriptions cancelled — they'll run until their period ends. Cancel App Store / Play Store subscriptions from their store.",
        );
      } else {
        toast.success("Subscriptions cancelled — they'll run until their period ends.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not cancel subscriptions');
    }
  };

  // "Have a code?" redeem form (AB#464).
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    setCodeError(null);
    try {
      const res = await redeem.mutateAsync(trimmed);
      setCode('');
      // useRedeemCode refetches billing status, so the comped tier reflects.
      toast.success(`Code applied — you're on ${cap(res.data?.effective_plan ?? 'your plan')}.`);
    } catch (err) {
      const mapped = err instanceof ApiError ? REDEEM_ERRORS[err.data.code ?? ''] : undefined;
      setCodeError(mapped ?? (err instanceof Error ? err.message : 'Could not redeem code'));
    }
  };

  // Webhook landed: the plan now matches what we bought — stop polling, show the
  // branded confirmation panel.
  useEffect(() => {
    // 'free' is never a purchase — a stale pendingUpgrade=free must not show
    // the thanks-for-upgrading panel.
    if (pendingTier && pendingTier !== 'free' && plan === pendingTier) {
      sessionStorage.removeItem(PENDING_UPGRADE_KEY);
      setPendingTier(null);
      setJustUpgraded(plan);
    }
  }, [pendingTier, plan]);

  // AB#489 desktop: hosted checkout opens in the system browser, so the webview
  // never navigates away and back. Re-poll entitlement status whenever the user
  // tabs back into the app, until the webhook flips the plan (see pendingTier).
  useEffect(() => {
    if (!isTauri() || !pendingTier) return;
    return onWindowFocus(() => {
      queryClient.invalidateQueries({ queryKey: billingKeys.status() });
    });
  }, [pendingTier, queryClient]);

  // Stop polling after a while so a never-arriving webhook can't spin forever.
  useEffect(() => {
    if (!pendingTier) return;
    const timer = setTimeout(() => {
      sessionStorage.removeItem(PENDING_UPGRADE_KEY);
      setPendingTier(null);
    }, 60_000);
    return () => clearTimeout(timer);
  }, [pendingTier]);

  // shortcut: '$79' is a placeholder until the backend sends ultimate_price (AB#1061).
  const priceFor = (id: BillingTier) =>
    id === 'ultimate'
      ? status?.ultimate_price ?? '$79'
      : id === 'pro'
        ? status?.pro_price ?? '$29'
        : status?.basic_price ?? '$7';
  const checkoutFor = (id: BillingTier) =>
    id === 'ultimate' ? checkoutUltimate : id === 'pro' ? checkoutPro : checkoutBasic;

  const handleUpgrade = (tier: BillingTier, url: string | undefined) => {
    if (!url) {
      toast.error('Checkout is not configured yet. Set the RevenueCat checkout URL.');
      return;
    }
    // Remember the tier so, on return, we poll until the webhook flips the plan.
    sessionStorage.setItem(PENDING_UPGRADE_KEY, tier);
    // AB#489: desktop hands off to the system browser (keeps the iframe on the
    // tauri origin so the poller above survives); web keeps the same-window nav.
    void startCheckout(url);
  };

  const seatPrice = status?.seat_price ?? '$1.99';

  const handleBuySeat = async (accountPid: string) => {
    try {
      const res = await seatCheckout.mutateAsync(accountPid);
      const url = res.data?.url;
      if (url) {
        await startCheckout(url);
      } else {
        toast.error('Seat checkout is not configured yet. Set the RevenueCat seat checkout URL.');
      }
    } catch (err) {
      // AB#602: the account still has a cancel-at-period-end seat — RC walls a
      // re-purchase, so route to reactivate instead of surfacing a dead error.
      const held = seatAlreadySubscribed(err);
      if (held) {
        if (held.manage_url) {
          toast.info('This account already has an extra seat set to cancel — opening it to reactivate.');
          void openBillingLink(held.manage_url);
        } else {
          toast.info(
            'This account already has an extra seat set to cancel at period end. Reactivate it instead of buying another.',
          );
        }
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Could not start seat checkout');
    }
  };

  return (
    <>
      {justUpgraded && (
        <Card className="border-primary">
          <CardContent className="relative flex flex-col items-center gap-3 py-8 text-center">
            <Button
              variant="ghost"
              size="icon-sm"
              className="absolute right-2 top-2"
              aria-label="Dismiss"
              onClick={() => setJustUpgraded(null)}
            >
              <X className="size-4" />
            </Button>
            <img
              src={logoBlack}
              alt="WordPuppi"
              className="dark:hidden"
              style={{ height: 'calc(var(--spacing) * 12)' }}
            />
            <img
              src={logoWhite}
              alt="WordPuppi"
              className="hidden dark:block"
              style={{ height: 'calc(var(--spacing) * 12)' }}
            />
            <div className="flex items-center gap-2 text-primary">
              <CircleCheck className="size-5" />
              <span className="text-lg font-semibold">
                You&apos;re on WordPuppi {justUpgraded.charAt(0).toUpperCase() + justUpgraded.slice(1)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Thanks for upgrading — your subscription is active and your new plan is ready to use.
            </p>
            <div className="flex flex-wrap justify-center gap-2 pt-1">
              <Button asChild>
                <Link to="/sites">Go to your sites</Link>
              </Button>
              {status?.manage_url && (
                <Button variant="outline" asChild>
                  <a href={status.manage_url} target="_blank" rel="noreferrer">
                    Manage subscription
                  </a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {pendingTier && !justUpgraded && (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          <span className="size-2 animate-pulse rounded-full bg-primary" aria-hidden="true" />
          Finishing your upgrade — this only takes a moment.
        </div>
      )}

      <div>
        <h1 className="text-2xl font-semibold">Billing</h1>
        <p className="text-sm text-muted-foreground">Manage your plan and subscription.</p>
      </div>

      {/* Mismatch: RC holds a live sub the stored plan doesn't reflect yet
          (AB#581). Reassures rather than alarms — role="status", not "alert". */}
      {mismatch && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
        >
          <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
          <span className="flex-1 min-w-[16rem]">
            We found an active subscription that isn&apos;t reflected on your account yet.
            We&apos;re verifying it — this usually resolves in a few minutes. Your access is safe.
          </span>
          <Button variant="outline" size="sm" onClick={handleRefreshStatus}>
            <RefreshCw className="size-3.5" />
            Refresh status
          </Button>
        </div>
      )}

      {/* Source of truth: the RC subscription list, not the stored plan (AB#581).
          Always rendered — every state, every plan value, comp or not. */}
      <Card>
        <CardHeader>
          <CardTitle>Your subscriptions</CardTitle>
          <CardDescription>Plan and add-on subscriptions on this account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Plan summary row */}
          {isLoading ? (
            <Skeleton className="h-7 w-24" />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {mismatch ? (
                <Badge variant="secondary" className="gap-1.5 font-normal">
                  <span
                    className="size-1.5 animate-pulse rounded-full bg-amber-500"
                    aria-hidden="true"
                  />
                  Verifying…
                </Badge>
              ) : (
                <Badge
                  variant={effectivePlan === 'free' ? 'secondary' : 'default'}
                  className="uppercase tracking-wider"
                >
                  {effectivePlan}
                </Badge>
              )}
              {isComp && (
                <Badge variant="secondary" className="font-normal">
                  Complimentary
                </Badge>
              )}
            </div>
          )}

          <Separator />

          {/* Subscription list */}
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : activeSubs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {isComp
                ? `No active subscriptions — your ${cap(effectivePlan)} access is complimentary.`
                : 'No active subscriptions. Pick a plan below to upgrade.'}
            </p>
          ) : (
            <div className="space-y-3">
              {activeSubs.map((sub) => (
                <SubscriptionRow key={sub.id} sub={sub} />
              ))}
              {overlap && (
                <div className="flex flex-wrap items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                  <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden="true" />
                  <span>
                    You have {planSubs.length} active plan subscriptions — you&apos;re likely being
                    double-billed. Keep one and cancel the rest from each subscription&apos;s Manage
                    link, or cancel everything below and re-subscribe clean.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Footer: cancel-all when subs exist, else the recovery escape hatch */}
          {!isLoading && (
            <>
              <Separator />
              {activeSubs.length > 0 ? (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    disabled={cancelSubs.isPending}
                    onClick={() => void handleCancelAll()}
                  >
                    {cancelSubs.isPending ? 'Cancelling…' : 'Cancel all subscriptions'}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-muted-foreground">
                  <span>Paying for a subscription you don&apos;t see here?</span>
                  <Button variant="outline" size="sm" onClick={handleRefreshStatus}>
                    <RefreshCw className="size-3.5" />
                    Refresh
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <a href="mailto:rick@webpuppi.com">Contact support</a>
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      {isComp && !isLoading && (
        <p className="-mt-3 px-1 text-xs text-muted-foreground">
          Your {cap(effectivePlan)} plan is complimentary — you&apos;re not being charged.
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Have a code?</CardTitle>
          <CardDescription>Redeem a promo or complimentary-access code.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRedeem} className="flex flex-wrap items-start gap-2">
            <div className="flex-1 min-w-[12rem] space-y-1">
              <Input
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (codeError) setCodeError(null);
                }}
                placeholder="Enter your code"
                aria-label="Promo or comp code"
                aria-invalid={codeError ? true : undefined}
                autoComplete="off"
                spellCheck={false}
              />
              {codeError && <p className="text-xs text-destructive">{codeError}</p>}
            </div>
            <Button type="submit" disabled={redeem.isPending || !code.trim()}>
              {redeem.isPending ? 'Redeeming...' : 'Redeem'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {ownedAccounts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Team seats</CardTitle>
            <CardDescription>
              Each account includes you plus one member. Additional seats are {seatPrice}/mo
              each.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {ownedAccounts.map((account) => (
              <div
                key={account.pid}
                className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg p-3"
              >
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">{account.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {account.member_count} of {account.member_cap} members ·{' '}
                    {/* AB#602: a cancelled-but-in-grace seat is shown honestly, not hidden as 0. */}
                    {account.seat_cancelled ? (
                      <span className="text-amber-700 dark:text-amber-400">
                        {account.extra_seats} {account.extra_seats === 1 ? 'seat' : 'seats'} —
                        cancelled
                        {account.seat_ends_at
                          ? `, ends ${new Date(account.seat_ends_at).toLocaleDateString()}`
                          : ''}
                      </span>
                    ) : (
                      `${account.extra_seats} extra ${account.extra_seats === 1 ? 'seat' : 'seats'}`
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {account.extra_seats > 0 && !account.seat_cancelled && status?.manage_url && (
                    <Button variant="ghost" size="sm" asChild>
                      <a href={status.manage_url} target="_blank" rel="noreferrer">
                        Manage
                      </a>
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={seatCheckout.isPending}
                    onClick={() => handleBuySeat(account.pid)}
                  >
                    {seatCheckout.isPending
                      ? 'Starting...'
                      : account.seat_cancelled
                        ? 'Reactivate seat'
                        : `Add seat · ${seatPrice}/mo`}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {TIERS.map((tier) => {
          // Compare against the effective plan so a comped Pro user sees Pro as
          // current instead of being upsold it (AB#464).
          const isCurrent = effectivePlan === tier.id;
          const co = checkoutFor(tier.id);
          const url = co.data?.data?.url;
          const notReady = !isCurrent && (isLoading || co.isLoading);
          // Backend refused re-purchase: a cancel-at-period-end sub is still live.
          const heldSub = isCurrent ? null : alreadySubscribed(co.error);
          // Backend refused: user is on a different paid plan — switch in the portal (AB#467).
          const switchInfo = isCurrent ? null : planSwitch(co.error);
          const manageHref = heldSub
            ? heldSub.manage_url || status?.manage_url
            : switchInfo
              ? switchInfo.manage_url || status?.manage_url
              : undefined;
          return (
            <Card key={tier.id} className={isCurrent ? 'border-primary' : undefined}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{tier.name}</CardTitle>
                  {isCurrent && <Badge>Current</Badge>}
                </div>
                <CardDescription>{tier.blurb}</CardDescription>
                <div className="pt-2">
                  <span className="text-3xl font-semibold">{priceFor(tier.id)}</span>
                  <span className="text-sm text-muted-foreground">/mo</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {heldSub ? (
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">
                      Your previous subscription is active
                      {heldSub.current_period_ends_at
                        ? ` until ${new Date(heldSub.current_period_ends_at).toLocaleDateString()}`
                        : ''}
                      .
                    </p>
                    {manageHref && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => void openBillingLink(manageHref)}
                        aria-label="Manage subscription (opens in browser)"
                      >
                        Manage subscription
                        <ExternalLink className="size-3.5" />
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Reactivate it there instead of buying again — contact{' '}
                      <a href="mailto:rick@webpuppi.com" className="underline">
                        support
                      </a>{' '}
                      if you need help.
                    </p>
                  </div>
                ) : switchInfo ? (
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">
                      You're on the{' '}
                      <span className="font-medium capitalize">{switchInfo.from || plan}</span>{' '}
                      plan. Switch plans from Manage subscription instead of buying a new one.
                    </p>
                    {manageHref && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => void openBillingLink(manageHref)}
                        aria-label="Manage subscription (opens in browser)"
                      >
                        Manage subscription
                        <ExternalLink className="size-3.5" />
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Need help switching? Contact{' '}
                      <a href="mailto:rick@webpuppi.com" className="underline">
                        support
                      </a>
                      .
                    </p>
                  </div>
                ) : (
                  <Button
                    className="w-full"
                    variant={isCurrent ? 'secondary' : 'default'}
                    // Purchases paused while a live sub is being reconciled (AB#581 §4.3);
                    // the real guard is the backend checkout_guard.
                    disabled={isCurrent || notReady || mismatch}
                    onClick={() => handleUpgrade(tier.id, url)}
                  >
                    {isCurrent ? 'Current plan' : `Upgrade to ${tier.name}`}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      {mismatch && (
        <p className="-mt-3 px-1 text-xs text-muted-foreground">
          Purchases are paused while we verify your existing subscription.
        </p>
      )}

      <PaymentTrust />
    </>
  );
}
